import React, { useState, useRef } from 'react'
import toastr from 'toastr'
import map from 'lodash/map'
import classnames from 'classnames'
import UploadArea from '@SiteComponents/UploadArea'
import Remove from '@Images/project_setup_form_remove_language.svg'
import Alert from '@Images/project_setup_form_alert.svg'
import ScriptFile from '@Images/project_setup_form_script_file.svg'

import './ImportScript.scss'

const MAX_FILE_SIZE = 2097152 // Bytes = 2MB
const PANEL_WIDTH = 117 // px, as in ImportScript.scss
const UPLOAD_FILE_PANEL_INDEX = 0
const PASTE_TEXT_PANEL_INDEX = 1
const PANEL_LABELS = ['Upload file', 'Paste text']

interface ImportScriptProps {
  readonly shouldImportScript: boolean
  readonly scriptText: string
  readonly showAlert: boolean
  readonly scriptFileName: string
  setShouldImportScript(newShouldImportScript: boolean): void
  setScriptText(newScriptText: string): void
  setScriptFileName(newScriptFileName: string): void
}

const ImportScript: React.FC<ImportScriptProps> = props => {
  const {
    showAlert,
    shouldImportScript,
    scriptText,
    scriptFileName,
  } = props

  const [uploadFile, setUploadFile] = useState(true)

  const fileRef = useRef<HTMLInputElement>(null)

  const scriptTextEmpty = scriptText.length === 0
  const shouldRenderScriptUploadArea = shouldImportScript && uploadFile && scriptTextEmpty
  const shouldRenderScript = shouldImportScript && uploadFile && !scriptTextEmpty
  const shouldRenderTextArea = shouldImportScript && !uploadFile

  const handleChangeShouldImportScript = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newShouldImportScript = e.target.checked
    props.setShouldImportScript(newShouldImportScript)

    if (!newShouldImportScript) {
      props.setScriptText('')
      setUploadFile(true)
    }
  }

  const handleFileImport = (files: File[]) => {
    if (files.length === 0) { return }

    if (files[0].size > MAX_FILE_SIZE) {
      toastr.error('Your file is too large (> 2MB).', 'Error')
      return
    }

    const fileReader = new FileReader()
    fileReader.onload = () => {
      const newScriptText = fileReader.result?.toString()

      if (!newScriptText || newScriptText.trim().length === 0) { return }

      props.setScriptText(newScriptText.trim())
      props.setScriptFileName(files[0].name)
    }

    fileReader.readAsText(files[0])
  }

  const handleImportClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = (e.target as HTMLInputElement).files
    if (!files) { return }

    handleFileImport(Array.from(files))

    e.target.value = '' // Set to an empty string to allow the upload of the same file again after aborting
  }

  const openFileManager = () => {
    if (!fileRef) { return }
    if (!fileRef.current) { return }

    fileRef.current.click()
  }

  const handleFileDrop = (filesList: FileList) => {
    handleFileImport(Array.from(filesList))
  }

  const changeScriptText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.setScriptText(e.target.value)
  }

  const resetScriptUpload = () => {
    props.setScriptText('')
    props.setScriptFileName('')
  }

  const stopPropagation = (e: React.KeyboardEvent) => {
    e.stopPropagation()
  }

  const renderImportScriptSwitch = () => {
    return (
      <div className="SetupProjectPopupTts__switch">
        <label className="SetupProjectPopupTts__switch-label">
          <input
            type="checkbox"
            checked={shouldImportScript}
            onChange={handleChangeShouldImportScript}
          />
          <span className="SetupProjectPopupTts__switch-slider" />
        </label>
        Import a script
      </div>
    )
  }

  const renderFileUploadSwitch = () => {
    let activePanelIndex = UPLOAD_FILE_PANEL_INDEX
    if (!uploadFile) {
      activePanelIndex = PASTE_TEXT_PANEL_INDEX
    }

    return (
      <React.Fragment>
        <div className="ImportScript__panels-wrapper">
          <ul className="ImportScript__panels">
            <div
              className="ImportScript__active-indicator"
              style={{
                transform: `translateX(${activePanelIndex * PANEL_WIDTH}px)`
              }}
            />
            {map(PANEL_LABELS, (panelLabel, index) => {
              const isActive = index === activePanelIndex
              const isUploadFilePanel = index === UPLOAD_FILE_PANEL_INDEX
              const isPasteTextPanel = index === PASTE_TEXT_PANEL_INDEX
              const switchToUploadFilePanel = isUploadFilePanel && !uploadFile
              const switchToPasteTextPanel = isPasteTextPanel && uploadFile

              const changePanel = () => {
                if (switchToUploadFilePanel) {
                  setUploadFile(true)
                  props.setScriptText('')
                } else if (switchToPasteTextPanel) {
                  setUploadFile(false)
                  resetScriptUpload()
                }
              }

              return (
                <li
                  key={index}
                  className={classnames(
                    'ImportScript__panel',
                    { 'ImportScript__panel-active': isActive }
                  )}
                  onClick={changePanel}
                >
                  {panelLabel}
                </li>
              )
            })}
          </ul>

        </div>
      </React.Fragment>
    )
  }

  const renderUploadArea = () => {
    return (
      <UploadArea
        openFileManager={openFileManager}
        handleFileDrop={handleFileDrop}
        showAlert={showAlert}
      />
    )
  }

  const renderScript = () => {
    return (
      <div className="ImportScript__script-wrapper">
        <div
          className="ImportScript__script"
          onClick={openFileManager}
        >
          <ScriptFile />
          <div className="ImportScript__script-name">
            {scriptFileName}
          </div>
        </div>
        <div
          className="SetupProjectPopup__button"
          onClick={resetScriptUpload}
        >
          <Remove />
        </div>
      </div>
    )
  }

  const renderTextArea = () => {
    return (
      <div className="ImportScript__textarea-wrapper">
        <textarea
          value={scriptText}
          placeholder="Paste your script here..."
          className = {classnames(
            'ImportScript__textarea',
            { 'ImportScript__textarea-alert': showAlert },
          )}
          onChange={changeScriptText}
          onKeyDown={stopPropagation}
        />
      </div>
    )
  }

  const renderAlert = () => {
    return (
      <div className="SetupProjectPopupTts__alert">
        <Alert />
        You need to upload or paste a script.
      </div>
    )
  }

  const renderImport = () => {
    return (
      <input
        ref={fileRef}
        type="file"
        className="ImportScript__upload-input"
        onChange={handleImportClick}
      />
    )
  }

  return (
    <div className="ImportScript">
      {renderImportScriptSwitch()}
      {shouldImportScript && renderFileUploadSwitch()}
      {(shouldRenderScriptUploadArea || shouldRenderTextArea) && (
        <div
          className="ImportScript__upload-and-textarea-wrapper"
        >
          {shouldRenderScriptUploadArea && renderUploadArea()}
          {shouldRenderTextArea && renderTextArea()}
        </div>
      )}
      {shouldRenderScript && renderScript()}
      {showAlert && renderAlert()}
      {renderImport()}
    </div>
  )
}

export default ImportScript
